import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/toast-custom.css';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import VerifyOtp from './pages/auth/VerifyOtp';
import ResetPassword from './pages/auth/ResetPassword';
import MainLayout from './layouts/MainLayout';
import AdminRoutes from './routes/AdminRoutes';
import RetailerRoutes from './routes/RetailerRoutes';
import SuperDistributorRoutes from './routes/SuperDistributorRoutes';
import MasterDistributorRoutes from './routes/MasterDistributorRoutes';
import DistributorRoutes from './routes/DistributorRoutes';
import ApiPartnerRoutes from './routes/ApiPartnerRoutes';
import SignupRoutes from './routes/SignupRoutes';
import { authService } from './services';
import { normalizeRole } from './utils/rolePermissions';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is logged in using authToken
    const isLoggedIn = authService.isAuthenticated();
    if (isLoggedIn) {
      const userRole = normalizeRole(authService.getUserRole());
      if (!userRole) {
        return;
      }
      const token = authService.getAuthToken();
      
      // Create basic user object from stored data
      setUser({
        role: userRole,
        token: token
      });
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser({
      ...userData,
      role: normalizeRole(userData?.role)
    });
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    authService.clearAuthData(); // Clear all auth data including token and role
  };

  // Function to get the appropriate routes based on user role
  const getRoutesByRole = () => {
    const userRole = normalizeRole(user?.role || authService.getUserRole());

    switch(userRole) {
      case 'admin':
        return <AdminRoutes />;
      case 'retailer':
        return <RetailerRoutes />;
      case 'super_distributor':
        return <SuperDistributorRoutes />;
      case 'master_distributor':
      case 'master':
        return <MasterDistributorRoutes />;
      case 'distributor':
        return <DistributorRoutes />;
      case 'api_partner':
        return <ApiPartnerRoutes />;
      default:
        return <Navigate to="/dashboard" replace />;
    }
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          {!isAuthenticated ? (
            <>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/signup/*" element={<SignupRoutes />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-otp" element={<VerifyOtp />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </>
          ) : (
            <>
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route path="/signup/*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/forgot-password" element={<Navigate to="/dashboard" replace />} />
              <Route path="/verify-otp" element={<Navigate to="/dashboard" replace />} />
              <Route path="/reset-password" element={<Navigate to="/dashboard" replace />} />
              <Route path="/*" element={<MainLayout onLogout={handleLogout} />}>
                <Route path="*" element={getRoutesByRole()} />
              </Route>
            </>
          )}
        </Routes>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

export default App;
